// Client-side mirror of the cf-worker `aggregates` module's headline + duration
// formatting. The worker module can't be imported here (different runtime), so
// this re-implements the same behavior. Behavior is locked by tests in
// __tests__/rap-sheet-format.test.js — keep both in sync if either side
// changes.

import { newsOrgFromOperator } from './news-operators';
import { pickSeeded } from './headline-seed';
import { NIGHT_WINDOW, plural } from './copy-contract';

/**
 * Format a duration in seconds.
 *   < 60s        -> "${s}s"            e.g. "30s"
 *   < 3600s      -> "${m}m"            e.g. "47m"
 *   exact hour   -> "${h}h"            e.g. "2h"
 *   otherwise    -> "${h}h ${m}m"      e.g. "8h 14m"
 */
export function formatDuration(seconds) {
  if (!Number.isFinite(seconds)) return '0s';
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    return `${m}m`;
  }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function aircraftSubject(aircraft) {
  if (!aircraft) return null;
  const newsOrg = newsOrgFromOperator(aircraft.operator);
  if (newsOrg) {
    return { subject: `${newsOrg}'s news chopper`, possessive: `${newsOrg}'s chopper`, isNews: true, key: newsOrg };
  }
  if (aircraft.registration) {
    return { subject: aircraft.registration, possessive: aircraft.registration, isNews: false, key: aircraft.registration };
  }
  if (aircraft.operator) {
    const op = aircraft.operator
      .replace(/\b(inc\.?|llc\.?|corp\.?|company|aviation|services|helicopters?)\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim() || aircraft.operator;
    return { subject: `A ${op} helicopter`, possessive: `A ${op} helicopter`, isNews: false, key: op };
  }
  return null;
}

/**
 * Build the rap-sheet headline.
 *
 * Two-arg form (legacy) returns aggregate-only phrasing. Three-arg form with
 * `{ aircraft, regionName }` context returns a subject-led headline that names
 * the aircraft and place explicitly.
 */
export function buildHeadline(totals, windowDays, context = {}) {
  const { nightEvents: n = 0, events: e = 0, hoverTimeS = 0 } = totals || {};
  const subj = aircraftSubject(context.aircraft);
  const where = context.regionName || null;

  // Subject-aware (new) form: only when we have BOTH a subject and a region.
  if (subj && where) {
    const seed = `${subj.key}|${where}|${windowDays}|${e}|${n}`;

    // 1. Single visit — short and specific.
    if (e === 1) {
      return {
        lead: pickSeeded(seed, [
          `${subj.subject} has been over ${where} once — for ${formatDuration(hoverTimeS)}.`,
          `One visit: ${subj.subject} spent ${formatDuration(hoverTimeS)} over ${where}.`,
          `${subj.subject} dropped in on ${where} once for ${formatDuration(hoverTimeS)}.`,
        ]),
        sub: `Last ${windowDays} days.`,
      };
    }
    // 2. News operator — neutral mention of the org + repetition.
    if (subj.isNews) {
      return {
        lead: pickSeeded(seed, [
          `${subj.subject} has logged ${e} ${plural(e, 'hover', 'hovers')} over ${where} in the last ${windowDays} days.`,
          `${subj.subject}: ${e} ${plural(e, 'hover', 'hovers')} over ${where} in ${windowDays} days.`,
          `${e} ${plural(e, 'visit', 'visits')} from ${subj.subject} to ${where} in the last ${windowDays} days.`,
        ]),
        sub: `${formatDuration(hoverTimeS)} total airtime.`,
      };
    }
    // 3. Heavy night repeat (>= 10 events, >= 5 nights) → "keeps coming back".
    if (e >= 10 && n >= 5) {
      return {
        lead: pickSeeded(seed, [
          `${subj.subject} keeps coming back to ${where} — ${e} hovers, ${n} after midnight.`,
          `${subj.subject} won't leave ${where} alone: ${e} hovers, ${n} of them overnight.`,
          `${subj.subject} has logged ${e} hovers over ${where}, ${n} of them after dark.`,
        ]),
        sub: `${formatDuration(hoverTimeS)} total over ${windowDays} days.`,
      };
    }
    // 4. High total airtime (>= 6 hours) — lead with the time.
    if (hoverTimeS >= 6 * 3600) {
      return {
        lead: pickSeeded(seed, [
          `${subj.subject} has spent ${formatDuration(hoverTimeS)} over ${where} in the last ${windowDays} days.`,
          `${formatDuration(hoverTimeS)} of ${subj.subject} over ${where} in ${windowDays} days.`,
          `${subj.subject} put in ${formatDuration(hoverTimeS)} over ${where} this past ${windowDays} days.`,
        ]),
        sub: `${e} ${plural(e, 'hover', 'hovers')} logged.`,
      };
    }
    // 5. Default subject-aware.
    return {
      lead: pickSeeded(seed, [
        `${subj.subject} has hovered over ${where} ${e} ${e === 1 ? 'time' : 'times'} in the last ${windowDays} days.`,
        `${subj.subject}: ${e} ${plural(e, 'hover', 'hovers')} over ${where} in ${windowDays} days.`,
        `${e} ${plural(e, 'visit', 'visits')} from ${subj.subject} to ${where} in the last ${windowDays} days.`,
      ]),
      sub: `${formatDuration(hoverTimeS)} total airtime.`,
    };
  }

  // Legacy aggregate-only fallback (kept for back-compat with tests + worker mirror).
  if (n > 0) {
    return {
      lead: `${n} ${plural(n, 'night', 'nights')} between ${NIGHT_WINDOW.rangeLabel}`,
      sub: `${e} ${plural(e, 'hover', 'hovers')} · ${formatDuration(hoverTimeS)} total`,
    };
  }
  return {
    lead: `${e} ${plural(e, 'hover', 'hovers')} in ${windowDays} days`,
    sub: `${formatDuration(hoverTimeS)} total`,
  };
}
